/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { MdSettings } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { formatDistance } from 'date-fns';

import {
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import CardSideBordered from '~/components/CardSideBordered';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';

import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';
import getDateLocale from '~/util/GetDateLocale';

function applySequentialOrders(list) {
    let nextOrder = 0;
    return list.map(item => {
        if (!item.checked) {
            return { ...item, order: '' };
        }
        const updated = { ...item, order: nextOrder };
        nextOrder += 1;
        return updated;
    });
}

export default function PluginspaceCardsDashboards({
    customPluginspaceId,
    asComponent,
    onSaveRef,
    onCanSaveChange,
}) {
    const dispatch = useDispatch();
    const settings = useSelector(state => state.settings || null);
    const pluginspaceId =
        customPluginspaceId ||
        (settings && settings.pluginspace && settings.pluginspace.id);
    const date_loc = getDateLocale(settings);

    const [isLoading, setIsLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [availableCards, setAvailableCards] = useState([]);

    const hasSelection = availableCards.some(card => card.checked);

    function requestError(error) {
        if (error && error.response) {
            const code = error.response.data && error.response.data.code;
            const message = code ? (
                <LocaleMessage msg={`errors.${code}`} />
            ) : (
                <LocaleMessage msg="errors.unknown" />
            );
            toast.error(message);
            if (error.response.status === 401) {
                dispatch(expireSession());
            }
        } else if (error && error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
        setIsLoading(false);
    }

    async function loadData() {
        if (!pluginspaceId) return;
        setIsLoading(true);
        try {
            const response = await api.get(
                `pluginspaces/${pluginspaceId}/cards_dashboards`
            );
            if (response.status === 200 && response.data) {
                const linkedMap = {};
                (response.data.linked || []).forEach(item => {
                    if (item.card_dashboard) {
                        linkedMap[item.card_dashboard.uid] = {
                            order: item.order,
                            updater_name: item.updater_name,
                            updated_at: item.updated_at,
                            active: item.active,
                        };
                    }
                });

                const allCards = (response.data.available || []).map(card => {
                    const isLinked = card.uid in linkedMap;
                    const isChecked = isLinked
                        ? linkedMap[card.uid].active
                        : false;
                    const order =
                        isLinked &&
                        linkedMap[card.uid].order !== undefined &&
                        linkedMap[card.uid].order !== null
                            ? linkedMap[card.uid].order
                            : '';
                    const updater_name = isLinked
                        ? linkedMap[card.uid].updater_name
                        : null;
                    const updated_at = isLinked
                        ? linkedMap[card.uid].updated_at
                        : null;
                    return {
                        card_dashboard_uid: card.uid,
                        name: card.name,
                        checked: isChecked,
                        order,
                        updater_name,
                        updated_at,
                    };
                });

                allCards.sort((a, b) => {
                    if (a.checked && b.checked) {
                        const orderA = a.order === '' ? 0 : Number(a.order);
                        const orderB = b.order === '' ? 0 : Number(b.order);
                        return orderA - orderB;
                    }
                    if (a.checked) return -1;
                    if (b.checked) return 1;
                    return a.name.localeCompare(b.name);
                });

                setAvailableCards(allCards);
            }
            setHasLoaded(true);
            setIsLoading(false);
        } catch (err) {
            requestError(err);
        }
    }

    useEffect(() => {
        loadData();
    }, [pluginspaceId, date_loc]);

    function handleCheckboxChange(cardUid) {
        setAvailableCards(prev =>
            prev.map(item => {
                if (item.card_dashboard_uid !== cardUid) {
                    return item;
                }
                const newChecked = !item.checked;
                if (!newChecked) {
                    return {
                        ...item,
                        checked: false,
                        order: '',
                    };
                }
                const maxOrder = prev.reduce(
                    (max, card) =>
                        card.checked
                            ? Math.max(
                                  max,
                                  card.order === '' ? 0 : Number(card.order)
                              )
                            : max,
                    -1
                );
                return {
                    ...item,
                    checked: true,
                    order: maxOrder + 1,
                };
            })
        );
    }

    function handleSelectAll() {
        const allSelected =
            availableCards.length > 0 && availableCards.every(c => c.checked);

        if (allSelected) {
            setAvailableCards(prev =>
                prev.map(item => ({
                    ...item,
                    checked: false,
                    order: '',
                }))
            );
            return;
        }

        setAvailableCards(prev => {
            let currentMax = prev.reduce(
                (max, c) =>
                    c.checked
                        ? Math.max(max, c.order === '' ? 0 : Number(c.order))
                        : max,
                -1
            );
            return prev.map(item => {
                if (!item.checked) {
                    currentMax += 1;
                    return {
                        ...item,
                        checked: true,
                        order: currentMax,
                    };
                }
                return item;
            });
        });
    }

    function handleOrderChange(cardUid, newOrder) {
        setAvailableCards(prev =>
            prev.map(item =>
                item.card_dashboard_uid === cardUid
                    ? {
                          ...item,
                          order: newOrder,
                      }
                    : item
            )
        );
    }

    function handleDragEnd(result) {
        if (!result.destination) {
            return;
        }
        if (result.destination.index === result.source.index) {
            return;
        }

        setAvailableCards(prev => {
            const reordered = Array.from(prev);
            const [moved] = reordered.splice(result.source.index, 1);
            reordered.splice(result.destination.index, 0, moved);
            return applySequentialOrders(reordered);
        });
    }

    function renderCardName(name) {
        if (name && name.indexOf('dashboard.card.') === 0) {
            return <LocaleMessage msg={name} />;
        }
        return name || '';
    }

    function renderUpdatedAt(row) {
        if (!row.updated_at) {
            return '-';
        }

        const distance = formatDistance(new Date(row.updated_at), new Date(), {
            addSuffix: true,
            locale: date_loc,
        });

        return (
            <>
                {row.updater_name || ''}{' '}
                <span style={{ opacity: 0.7, fontSize: '12px' }}>
                    ({distance})
                </span>
            </>
        );
    }

    const handleSaveOrdering = useCallback(async () => {
        if (isLoading || !hasLoaded) {
            return false;
        }

        const checkedCards = availableCards.filter(c => c.checked);
        if (checkedCards.length === 0) {
            return false;
        }

        setIsLoading(true);
        try {
            const relationsPayload = checkedCards.map(item => ({
                card_dashboard_uid: item.card_dashboard_uid,
                order:
                    item.order === '' || Number.isNaN(Number(item.order))
                        ? 0
                        : parseInt(item.order, 10),
            }));

            await api.put(`pluginspaces/${pluginspaceId}/cards_dashboards`, {
                relations: relationsPayload,
            });

            toast.success(
                <LocaleMessage msg="page.pluginspace_dashboards.save_ordering_success" />
            );
            loadData();
            return true;
        } catch (err) {
            requestError(err);
            return false;
        }
    }, [availableCards, pluginspaceId, isLoading, hasLoaded]);

    useEffect(() => {
        if (onSaveRef) {
            onSaveRef.current = handleSaveOrdering;
        }
    }, [handleSaveOrdering, onSaveRef]);

    useEffect(() => {
        if (onCanSaveChange) {
            onCanSaveChange(hasLoaded && hasSelection);
        }
    }, [hasLoaded, hasSelection, onCanSaveChange]);

    const allSelected =
        availableCards.length > 0 && availableCards.every(c => c.checked);
    const someSelected = availableCards.some(c => c.checked);

    const innerContent = (
        <div className="row">
            <div className={asComponent ? 'col-12' : 'col-md-10 col-12 mb-5'}>
                <CardSideBordered
                    title={
                        <LocaleMessage msg="page.pluginspace_dashboards.active_cards_title" />
                    }
                    Icon={MdSettings}
                    hide={asComponent}
                >
                    <>
                        {availableCards.length === 0 ? (
                            <p
                                style={{
                                    padding: '10px 0',
                                    margin: 0,
                                    opacity: 0.7,
                                }}
                            >
                                <LocaleMessage msg="page.pluginspace_dashboards.no_active_cards" />
                            </p>
                        ) : (
                            <>
                                {hasLoaded && !hasSelection ? (
                                    <Alert
                                        severity="warning"
                                        variant="standard"
                                        style={{ marginBottom: 16 }}
                                    >
                                        <LocaleMessage msg="page.pluginspace_dashboards.min_one_card" />
                                    </Alert>
                                ) : null}
                                <p
                                    style={{
                                        margin: '0 0 12px',
                                        opacity: 0.7,
                                        fontSize: 13,
                                    }}
                                >
                                    <LocaleMessage msg="label.reorder" />
                                </p>
                                <DragDropContext onDragEnd={handleDragEnd}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    style={{ width: '80px' }}
                                                    align="center"
                                                >
                                                    <Checkbox
                                                        checked={allSelected}
                                                        indeterminate={
                                                            someSelected &&
                                                            !allSelected
                                                        }
                                                        onChange={
                                                            handleSelectAll
                                                        }
                                                        color="primary"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <LocaleMessage msg="table.headers.name" />
                                                </TableCell>
                                                <TableCell
                                                    style={{ width: '260px' }}
                                                >
                                                    <LocaleMessage msg="table.headers.updated" />
                                                </TableCell>
                                                <TableCell
                                                    style={{ width: '120px' }}
                                                >
                                                    <LocaleMessage msg="table.headers.order" />
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <Droppable droppableId="dashboard-cards">
                                            {dropProvided => (
                                                <TableBody
                                                    ref={dropProvided.innerRef}
                                                    {...dropProvided.droppableProps}
                                                >
                                                    {availableCards.map(
                                                        (row, index) => (
                                                            <Draggable
                                                                key={
                                                                    row.card_dashboard_uid
                                                                }
                                                                draggableId={String(
                                                                    row.card_dashboard_uid
                                                                )}
                                                                index={index}
                                                            >
                                                                {(
                                                                    dragProvided,
                                                                    dragSnapshot
                                                                ) => (
                                                                    <TableRow
                                                                        ref={
                                                                            dragProvided.innerRef
                                                                        }
                                                                        {...dragProvided.draggableProps}
                                                                        {...dragProvided.dragHandleProps}
                                                                        style={{
                                                                            background: dragSnapshot.isDragging
                                                                                ? '#f5f5f5'
                                                                                : undefined,
                                                                            cursor:
                                                                                'grab',
                                                                            ...dragProvided
                                                                                .draggableProps
                                                                                .style,
                                                                        }}
                                                                    >
                                                                        <TableCell align="center">
                                                                            <Checkbox
                                                                                checked={
                                                                                    row.checked
                                                                                }
                                                                                onChange={() =>
                                                                                    handleCheckboxChange(
                                                                                        row.card_dashboard_uid
                                                                                    )
                                                                                }
                                                                                color="primary"
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {renderCardName(
                                                                                row.name
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {renderUpdatedAt(
                                                                                row
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <TextField
                                                                                type="number"
                                                                                value={
                                                                                    row.order
                                                                                }
                                                                                disabled={
                                                                                    !row.checked
                                                                                }
                                                                                onChange={e =>
                                                                                    handleOrderChange(
                                                                                        row.card_dashboard_uid,
                                                                                        e
                                                                                            .target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                                inputProps={{
                                                                                    min: 0,
                                                                                    style: {
                                                                                        width:
                                                                                            '80px',
                                                                                    },
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                            </Draggable>
                                                        )
                                                    )}
                                                    {dropProvided.placeholder}
                                                </TableBody>
                                            )}
                                        </Droppable>
                                    </Table>
                                </DragDropContext>
                                {asComponent ? null : (
                                    <div
                                        className="mt-4"
                                        style={{
                                            marginTop: '20px',
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSaveOrdering}
                                            disabled={
                                                !hasLoaded || !hasSelection
                                            }
                                        >
                                            <LocaleMessage msg="button.save" />
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                </CardSideBordered>
            </div>
        </div>
    );

    if (asComponent) {
        return innerContent;
    }

    return (
        <PageContent
            title={<LocaleMessage msg="page.pluginspace_dashboards.title" />}
            loading={isLoading}
        >
            {innerContent}
        </PageContent>
    );
}
